import { CitationStyleId, NUMERIC_STYLE_IDS } from './styles';

export type BibliographyEntry = {
  id: string;
  cslJson: Record<string, unknown>;
};

function yearFromIssued(issued: unknown): string {
  if (
    issued &&
    typeof issued === 'object' &&
    Array.isArray((issued as { 'date-parts'?: unknown })['date-parts'])
  ) {
    const parts = (issued as { 'date-parts': number[][] })['date-parts'][0];
    if (parts && parts[0] != null) return String(parts[0]);
  }
  return '0000';
}

function firstAuthorKey(item: Record<string, unknown>): string {
  const authors = item.author as
    | Array<{ family?: string; given?: string; literal?: string }>
    | undefined;
  const first = authors?.[0];
  if (!first) return '';
  return (first.literal || first.family || first.given || '').toLowerCase();
}

function titleKey(item: Record<string, unknown>): string {
  return String(item.title || '').toLowerCase();
}

/**
 * Sort bibliography entries per the active style: numeric styles stay in
 * citation (insertion) order; author-date / notes styles sort by author,
 * then year, then title.
 */
export function sortBibliography(
  entries: BibliographyEntry[],
  styleId: CitationStyleId,
): BibliographyEntry[] {
  if (NUMERIC_STYLE_IDS.has(styleId)) {
    return entries.slice();
  }
  return entries.slice().sort((left, right) => {
    const authorCmp = firstAuthorKey(left.cslJson).localeCompare(
      firstAuthorKey(right.cslJson),
      'en',
      { sensitivity: 'base' },
    );
    if (authorCmp !== 0) return authorCmp;
    const yearCmp = yearFromIssued(left.cslJson.issued).localeCompare(
      yearFromIssued(right.cslJson.issued),
    );
    if (yearCmp !== 0) return yearCmp;
    return titleKey(left.cslJson).localeCompare(titleKey(right.cslJson), 'en', {
      sensitivity: 'base',
    });
  });
}

/** Prefix used when displaying / copying a numeric-style bibliography list. */
export function numberReference(
  styleId: CitationStyleId,
  index: number,
  html: string,
): string {
  const n = index + 1;
  if (styleId === 'ieee') return `[${n}] ${html}`;
  if (styleId === 'ama' || styleId === 'acs') return `${n}. ${html}`;
  return html;
}

let nextEntryId = 0;

export function nextBibliographyId(): string {
  nextEntryId += 1;
  return `bibl-${nextEntryId}`;
}
